import { useEffect, useState } from "react";
import { CalendarDays, CheckCircle2, ShieldCheck, IndianRupee } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/api";

const TIMES = ["10:00", "10:30", "11:00", "11:30", "12:00", "12:30", "15:00", "15:30", "16:00", "16:30", "17:00", "17:30", "18:00", "18:30", "19:00", "19:30"];
const todayISO = () => new Date().toISOString().split("T")[0];

const loadRazorpay = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => resolve(true);
  s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

export default function BookAppointment() {
  const [treatments, setTreatments] = useState([]);
  const [config, setConfig] = useState({ consultation_fee: 300, enabled: false });
  const [form, setForm] = useState({ name: "", email: "", phone: "", date: todayISO(), time: "10:00", treatment: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    document.title = "Book Appointment — Manasvi Dental Clinic";
    Promise.all([api.get("/treatments"), api.get("/payments/config")]).then(([tr, cfg]) => {
      setTreatments(tr.data);
      setForm(f => ({ ...f, treatment: tr.data[0]?.name || "" }));
      setConfig(cfg.data);
    }).catch(() => {});
    loadRazorpay();
  }, []);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.treatment) { toast.error("Please fill all required fields"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/create-order", form);

      // Mock mode (no Razorpay keys configured)
      if (data.mock) {
        setDone(data.appointment);
        toast.success("Appointment booked (test mode)");
        return;
      }

      // Open Razorpay checkout
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Payment gateway failed to load"); return; }

      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "Manasvi Dental Clinic",
        description: `Consultation booking - ${form.treatment}`,
        order_id: data.order_id,
        prefill: { name: data.name, email: data.email, contact: data.phone },
        theme: { color: "#7A8B76" },
        handler: async (resp) => {
          try {
            const verify = await api.post("/payments/verify", {
              appointment_id: data.appointment_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            setDone(verify.data.appointment);
            toast.success("Payment successful — appointment confirmed!");
          } catch (err) {
            toast.error("Payment verification failed. Please contact clinic.");
          }
        },
        modal: {
          ondismiss: () => { toast.info("Payment cancelled"); setLoading(false); },
        },
      };
      const rz = new window.Razorpay(options);
      rz.on("payment.failed", () => toast.error("Payment failed. Please try again."));
      rz.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Booking failed");
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <section className="max-w-3xl mx-auto px-6 py-24 anim-fade-up">
        <div className="bg-card border border-border rounded-2xl p-12 text-center" data-testid="booking-success">
          <div className="w-16 h-16 rounded-full bg-primary/15 grid place-items-center mx-auto mb-6"><CheckCircle2 className="w-8 h-8 text-primary" /></div>
          <h1 className="font-serif text-4xl">Appointment {done.payment_status === "paid" ? "Confirmed" : "Received"}</h1>
          <p className="mt-4 text-muted-foreground">Thank you <strong className="text-foreground">{done.name}</strong>. Your slot for <strong className="text-foreground">{done.treatment}</strong> on <strong className="text-foreground">{done.date}</strong> at <strong className="text-foreground">{done.time}</strong> is booked.</p>
          {done.payment_status === "paid" && <p className="mt-2 text-primary text-sm">✓ Payment of ₹{done.amount} received (Ref: {done.payment_id})</p>}
          <p className="mt-2 text-muted-foreground text-sm">Confirmation sent to <strong className="text-foreground">{done.email}</strong>.</p>
          <Button onClick={() => setDone(null)} className="mt-8 rounded-full" data-testid="book-another">Book Another</Button>
        </div>
      </section>
    );
  }

  return (
    <div className="anim-fade-up">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-8">
        <span className="text-xs uppercase tracking-[0.25em] text-primary">Book Appointment</span>
        <h1 className="font-serif text-5xl sm:text-6xl tracking-tight mt-3 max-w-3xl">Reserve your slot with Dr. Deepika.</h1>
        <p className="mt-6 text-lg text-muted-foreground max-w-2xl">Fill the form below — you&apos;ll receive an instant email &amp; SMS confirmation.</p>
      </section>

      <section className="max-w-4xl mx-auto px-6 lg:px-10 pb-20">
        <form onSubmit={onSubmit} className="bg-card border border-border rounded-2xl p-8 md:p-10 space-y-6" data-testid="booking-form">
          <div className="grid md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="b-name">Full Name*</Label>
              <Input id="b-name" name="name" value={form.name} onChange={onChange} required data-testid="book-name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="b-phone">Phone*</Label>
              <Input id="b-phone" name="phone" value={form.phone} onChange={onChange} required data-testid="book-phone" placeholder="+91 98XXXXXXXX" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="b-email">Email*</Label>
              <Input id="b-email" type="email" name="email" value={form.email} onChange={onChange} required data-testid="book-email" />
            </div>
            <div className="space-y-2">
              <Label>Treatment*</Label>
              <Select value={form.treatment} onValueChange={(v) => setForm({ ...form, treatment: v })}>
                <SelectTrigger data-testid="book-treatment"><SelectValue placeholder="Choose treatment" /></SelectTrigger>
                <SelectContent>
                  {treatments.map(t => <SelectItem key={t.id} value={t.name} data-testid={`treatment-opt-${t.slug}`}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="b-date">Preferred Date*</Label>
              <Input id="b-date" type="date" name="date" min={todayISO()} value={form.date} onChange={onChange} required data-testid="book-date" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Preferred Time*</Label>
              <Select value={form.time} onValueChange={(v) => setForm({ ...form, time: v })}>
                <SelectTrigger data-testid="book-time"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIMES.map(t => <SelectItem key={t} value={t} data-testid={`time-opt-${t}`}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="b-msg">Additional Message</Label>
              <Textarea id="b-msg" name="message" value={form.message} onChange={onChange} rows={4} data-testid="book-message" placeholder="Any specific concern? (optional)" />
            </div>
          </div>

          {/* Payment summary */}
          <div className="bg-muted/60 border border-border rounded-xl p-5 flex items-center gap-4" data-testid="booking-payment-summary">
            <div className="w-11 h-11 rounded-full bg-primary/15 grid place-items-center shrink-0">
              <IndianRupee className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1">
              <div className="font-medium">Consultation Booking Fee</div>
              <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                <ShieldCheck className="w-3 h-3" /> Secured by Razorpay {!config.enabled && <span className="ml-1 text-accent-foreground bg-accent/40 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest">Test Mode</span>}
              </div>
            </div>
            <div className="font-serif text-2xl">₹{config.consultation_fee}</div>
          </div>

          <Button type="submit" size="lg" className="w-full rounded-full bg-primary hover:bg-primary/90 h-12" disabled={loading} data-testid="book-submit">
            <CalendarDays className="mr-2 w-4 h-4" /> {loading ? "Processing..." : `Pay ₹${config.consultation_fee} & Book`}
          </Button>
          <p className="text-center text-xs text-muted-foreground">Refundable if cancelled 24+ hours before appointment. UPI, cards, netbanking &amp; wallets accepted.</p>
        </form>
      </section>
    </div>
  );
}
